import React, { useState } from 'react';

import { Article } from '@/modules/articles/model/article';
import ArticleView from '@/modules/articles/components/articles/article-view';
import BulletList from '@/modules/articles/components/scrollers/bullet-list/bullet-list';
import BulletListTitle from '@/modules/articles/components/scrollers/bullet-list/bullet-list-title';
import { RouteObject } from 'react-router-dom';
import { faEye, faFilter, faList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  BaseScroller,
  BaseScrollerState,
} from '@/modules/articles/widgets/scrollers/base-scroller';
import { WidgetContainerContext, ViewModeMenuItems } from '@/core/widgets/export';
import { FilterGroup } from '@/modules/articles/model/concept-item';
import { MenuList, CollapsibleMenuRow } from '@/shared/components/export';
import SectionFilters from '@/modules/articles/components/scrollers/section-filters';
import AdvancedFilterCategories from '@/modules/articles/components/scrollers/advanced-filter-categories';

function BulletListMainView({
  section,
  includedSections,
  filters,
}: {
  section: string[];
  includedSections: string[];
  filters?: FilterGroup[];
}) {
  const [article, setArticle] = useState<Article | undefined>();

  if (article)
    return (
      <div className={'h-100 '}>
        <ArticleView article={article} onClose={() => setArticle(undefined)} />
      </div>
    );

  return (
    <div className={'h-100 '}>
      <BulletList
        dark={true}
        section={section}
        filters={filters}
        onArticleClick={(a: Article) => setArticle(a)}
      />
    </div>
  );
}

export class BulletListWidget extends BaseScroller {
  private readonly filters?: FilterGroup[];

  constructor(
    id: string,
    section: string[],
    initialState: BaseScrollerState,
    _shortTitle: string,
    filters?: FilterGroup[],
  ) {
    super(`/bullets/${id}`, section, initialState, _shortTitle);
    this.filters = filters;
  }

  get headerPanel(): JSX.Element {
    return (
      <WidgetContainerContext.Consumer>
        {widgetContainer => (
          <>
            <FontAwesomeIcon icon={faList} />{' '}
            <BulletListTitle
              title={
                (widgetContainer.getState<BaseScrollerState>()?.title ?? this.defaultTitle) || ''
              }
              section={this.section}
            />
          </>
        )}
      </WidgetContainerContext.Consumer>
    );
  }

  headerActions({ children }: { children?: React.ReactNode }): JSX.Element {
    return (
      <WidgetContainerContext.Consumer>
        {widgetContainer => {
          const sections = widgetContainer.getState<BaseScrollerState>()?.sections;
          const visibleGroups = widgetContainer.getState<BaseScrollerState>()?.visibleGroups;
          const hasSectionPills = !!sections && sections.length > 1;
          const hasCategories = !!visibleGroups && visibleGroups.length > 0;

          return (
            <>
              {(hasSectionPills || hasCategories) && (
                <MenuList
                  trigger={<FontAwesomeIcon icon={faFilter} />}
                  toggleClassName={'btn btn-link p-2 text-primary-500'}
                  toggleTitle={'Filter'}
                  align={'right'}
                  footer={
                    <>
                      {hasSectionPills && (
                        <SectionFilters
                          disabled={false}
                          widgetId={widgetContainer.widgetId}
                          sections={sections!}
                          onFilteredDataNameChange={s =>
                            widgetContainer.updateState<BaseScrollerState>({ title: s })
                          }
                        />
                      )}
                      {hasSectionPills && hasCategories && <hr className={'menu-list-divider'} />}
                      {hasCategories && <AdvancedFilterCategories visibleGroups={visibleGroups!} />}
                    </>
                  }
                />
              )}
              {children}
            </>
          );
        }}
      </WidgetContainerContext.Consumer>
    );
  }

  menuActions(close: () => void) {
    return (
      <CollapsibleMenuRow label={'View'} icon={faEye}>
        <ViewModeMenuItems onSelect={close} depth={1} />
      </CollapsibleMenuRow>
    );
  }

  get routes(): RouteObject[] {
    return [
      {
        path: `/`,
        element: (
          <BulletListMainView
            section={this.section}
            includedSections={this.section}
            filters={this.filters}
          />
        ),
      },
    ];
  }
  get defaultRoute(): string {
    return `/`;
  }
}
